/**
 * Nexus Page Layout
 *
 * Reusable page component with sticky header and scrollable body,
 * similar to chat UI pattern with messages container and input.
 */

'use client'

import { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { useDeviceDetection } from '@/lib/mobile-optimization'

interface NexusPageLayoutProps {
  header: ReactNode
  children: ReactNode
  className?: string
  contentClassName?: string
  /** When true, content grows with children so layout scrolls and content passes behind sticky header */
  contentScrollsWithPage?: boolean
}

export function NexusPageLayout({
  header,
  children,
  className,
  contentClassName,
  contentScrollsWithPage = false
}: NexusPageLayoutProps) {
  const { isMobile } = useDeviceDetection()

  return (
    <div className={cn('flex size-full flex-col overflow-y-auto', className)}>
      {/* Sticky Header - z-20 so it stays above sticky table headers (z-10) */}
      <div
        className={cn(
          'bg-background/90 sticky top-0 z-20 shrink-0 backdrop-blur-sm',
          isMobile ? 'p-4 pb-5' : 'p-5 pb-6'
        )}
      >
        {/* Fade gradient at bottom */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-full bg-[linear-gradient(to_bottom,hsl(var(--background))_0%,hsl(var(--background))_75%,transparent_100%)]" />
        <div className="relative z-10 mx-auto w-full max-w-[1600px]">{header}</div>
      </div>

      {/* Content - use contentClassName for flex/min-h-0; contentScrollsWithPage lets content grow so it scrolls behind header */}
      <main
        className={cn(
          'flex min-h-0 flex-col',
          !contentScrollsWithPage && 'flex-1',
          isMobile ? 'p-4' : 'p-5',
          contentClassName
        )}
      >
        <div
          className={cn(
            'mx-auto min-h-0 w-full max-w-[1600px] flex-col',
            contentScrollsWithPage ? 'flex' : 'flex size-full flex-1'
          )}
        >
          {children}
        </div>
      </main>
    </div>
  )
}
